import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import useIsMobile from '../hooks/useIsMobile'

// The only sanctioned way to say yes and no.
//
// Ten dialogs had grown seven different Cancels and five different Confirms,
// and the colours had inverted along the way: Cancel was a filled red pill in
// eight places, while "archive these products off the live storefront" was a
// filled green one. Colour carried no information, because green meant confirm
// in five dialogs and red meant confirm in three.
//
// Passing actions as data rather than children is the point. There is nowhere
// to put a <Typography> child, no colour prop to reach for, and no way to make
// cancel the loudest control -- the rules are the component, not a convention
// someone has to remember.

// Green is deliberately absent. It survives as status (present, eligible,
// completed) and never as an action.
const TONES = {
    primary: 'primary',
    danger: 'error'
}

const MIN_TARGET = 44

function toButton(action, { variant, color, key }) {
    if (!action) return null
    const { label, onClick, disabled, startIcon, type } = action

    return (
        <Button
            key={key}
            type={type || 'button'}
            variant={variant}
            color={color}
            onClick={onClick}
            disabled={disabled}
            startIcon={startIcon}
            // Equal widths, so a row never comes out as three different sizes.
            sx={{ flex: 1, minHeight: MIN_TARGET }}
        >
            {label}
        </Button>
    )
}

export default function ActionBar(props) {
    const { cancel, confirm, secondary } = props
    const isMobile = useIsMobile()

    // Anything that is not an explicit danger is primary. An unknown tone must
    // not silently fall through to some other colour.
    const confirmColor = TONES[confirm?.tone] || TONES.primary

    return (
        <Box
            data-testid='action-bar'
            sx={{
                display: 'flex',
                gap: 1.5,
                // Stacked, the primary belongs on top where the thumb is, so the
                // DOM keeps its cancel-then-confirm reading order and the column
                // is reversed instead.
                flexDirection: isMobile ? 'column-reverse' : 'row'
            }}
        >
            {toButton(cancel, { key: 'cancel', variant: 'text', color: 'inherit' })}
            {toButton(secondary, { key: 'secondary', variant: 'outlined', color: 'inherit' })}
            {toButton(confirm, { key: 'confirm', variant: 'contained', color: confirmColor })}
        </Box>
    )
}
